import React, { FC, useRef, useState } from 'react';
import ModalComponent from '../../../../modal/ModalComponent';
import { FormattedMessage, useIntl } from 'react-intl';
import { Form as bootstrapForm, Form } from 'react-bootstrap';
import Label from '../../../../library/inputs/label/Label';
import { addErrorToast, addSuccessToast } from '../../../../errors/toast/ToastUtils';
import { useDispatch } from 'react-redux';
import { setOrganization } from '../../../../../slices/organization';
import { postOrganization } from '../../../../../services/organization/OrganizationService';
import { Formik, FormikProps } from 'formik';
import { history } from '../../../../../history';
import Avatar from '../../../avatar/Avatar';
import * as Yup from 'yup';
import OptionList from '../../../../library/inputs/optionlist/OptionList';

export interface Props {
  show: boolean;
  onClose: () => void;
}

interface Values {
  name: string;
}

const AddOrganizationModal: FC<Props> = ({ show, onClose }: Props) => {
  const intl = useIntl();
  const formRef = useRef<FormikProps<Values> | null>(null);
  const [newAvatarUrl, setNewAvatarUrl] = useState<string>('');
  const [organizationAvatar, setOrganizationAvatar] = useState<File>();
  const [currency, setCurrency] = useState<string>('usd');
  const dispatch = useDispatch();

  const resetModal = () => {
    onClose();
    setOrganizationAvatar(undefined);
    setNewAvatarUrl('');
  };

  const handleAddOrganization = async (organizationName) => {
    if (organizationAvatar) {
      const formData: FormData = new FormData();
      formData.append('organization', JSON.stringify({ name: organizationName, currency: currency }));
      formData.append('avatar', organizationAvatar as File);
      await postOrganization(formData)
        .then(async (response) => {
          if (response.status == 200) {
            addSuccessToast({
              message: intl.formatMessage({ id: 'organizationMenu.modal.addOrganizationSuccess' }),
            });
            dispatch(setOrganization(response.data));
            resetModal();
            history.push('/registrations');
          }
        })
        .catch(() => {
          addErrorToast({
            message: intl.formatMessage({ id: 'organizationMenu.modal.addOrganizationError' }),
          });
        });
    } else {
      addErrorToast({
        message: intl.formatMessage({ id: 'organizationMenu.modal.addOrganizationError' }),
      });
    }
  };

  const handleAvatarFileChange = (event) => {
    setOrganizationAvatar(event.target.files[0]);
    const newAvatar = URL.createObjectURL(event.target.files[0]);
    setNewAvatarUrl(newAvatar);
  };

  const emptyImage = () => {
    return (
      <div className="circle">
        <label className="file-label" htmlFor="inputGroupFile01">
          {intl.formatMessage({ id: 'organizationMenu.modal.import' })}
        </label>
      </div>
    );
  };

  const importedImage = () => {
    return <Avatar src={newAvatarUrl} width={96} height={96}></Avatar>;
  };

  const FormCreationErrorMessages = Yup.object().shape({
    name: Yup.string()
      .min(1, intl.formatMessage({ id: 'formSettings.errorMessage.short' }))
      .max(100, intl.formatMessage({ id: 'formSettings.errorMessage.long' }))
      .required(intl.formatMessage({ id: 'formSettings.errorMessage.required' })),
  });

  return (
    <ModalComponent
      closeSettings={() => resetModal()}
      buttons={[
        {
          text: intl.formatMessage({ id: 'organizationMenu.modal.addOrganization' }),
          buttonVariant: 'primary',
          onClick: () => formRef.current?.submitForm(),
        },
      ]}
      modalHeading={intl.formatMessage({
        id: 'organizationMenu.addOrganization',
      })}
      show={show}
    >
      <div className="d-flex justify-content-center flex-column">
        <Formik
          validationSchema={FormCreationErrorMessages}
          initialValues={{
            name: '',
          }}
          onSubmit={({ name }) => handleAddOrganization(name)}
          innerRef={(ref) => (formRef.current = ref)}
          validateOnChange
        >
          {({ errors, touched, values, handleChange, handleBlur }) => (
            <Form>
              <bootstrapForm.Group className="d-flex flex-column justify-content-center align-items-center">
                <div className="label-wrapper">
                  <Label label={intl.formatMessage({ id: 'organizationMenu.modal.avatar' })} />
                </div>
                <div className="logo-wrapper">
                  <div className="image-wrapper">
                    <input
                      type="file"
                      accept="image/png, image/jpeg"
                      onChange={(e) => handleAvatarFileChange(e)}
                      className="custom-file-input"
                      id="inputGroupFile01"
                    />
                    <label className="logo-label-input" htmlFor="inputGroupFile01">
                      {newAvatarUrl != '' ? importedImage() : emptyImage()}
                    </label>
                  </div>
                </div>
                <div className="d-flex flex-column align-items-center justify-content-center pt-5 px-5">
                  <Label label={intl.formatMessage({ id: 'organizationMenu.modal.name' })}></Label>
                  <bootstrapForm.Control
                    type="input"
                    name="name"
                    placeholder={intl.formatMessage({ id: 'organizationMenu.modal.name' })}
                    isInvalid={touched.name && !!errors.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.name}
                  />
                  <bootstrapForm.Control.Feedback className="text-center" type="invalid">
                    {errors.name && touched.name && (
                      <FormattedMessage id="organizationMenu.modal.errorName"></FormattedMessage>
                    )}
                  </bootstrapForm.Control.Feedback>
                  <div className="align-items-center px-5 pt-3">
                    <Label label={intl.formatMessage({ id: 'organizationMenu.modal.currency' })}></Label>
                    <div className="d-flex">
                      <OptionList
                        value={[currency]}
                        options={[
                          { text: 'USD', value: 'usd' },
                          { text: 'CAD', value: 'cad' },
                        ]}
                        onChange={(values) => {
                          if (values[0]) {
                            setCurrency(values[0]);
                          }
                        }}
                        multiSelection={false}
                        className="mx-2"
                      ></OptionList>
                    </div>
                  </div>
                </div>
              </bootstrapForm.Group>
            </Form>
          )}
        </Formik>
      </div>
    </ModalComponent>
  );
};

export default AddOrganizationModal;
